import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Content,
  Form,
  FormAlert,
  FormGroup,
  FormHelperText,
  FormSection,
  Grid,
  GridItem,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  TextInput,
} from '@patternfly/react-core';
import { UndoIcon } from '@patternfly/react-icons';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAccountingAccounts, useSaveAccountingAccounts } from '../../../hooks/useAdminAccountingAccounts';
import type { AccountingAccount } from '../../../types/models/AccountingAccount';
import { formatInstantBrussels } from '../../../utils/dateFormat';
import { ACCOUNT_ROLE_LABELS, WINAUDITOR_TYPE_LABELS, labelOf } from './reconciliationLabels';
import {
  buildAccountsPatch,
  defaultDraft,
  draftFrom,
  isAtDefault,
  isDraftValid,
  labelError,
  numberError,
  thirdPartyCodeError,
} from './accountingAccountsForm';
import type { AccountDraft, AccountsDraft } from './accountingAccountsForm';

interface AccountingAccountsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful save: every entry line uses these numbers, so the entries must be re-read. */
  onSaved: () => void;
}

/** How WinAuditor encodes the role, in French. */
function winauditorTypeText(account: AccountingAccount): string {
  const type = `${labelOf(WINAUDITOR_TYPE_LABELS, account.winauditor_type)} (${account.winauditor_type})`;
  switch (account.winauditor_type) {
    case 'C':
      return `WinAuditor : ${type} — chaque client y a son propre code (« Codes clients WinAuditor »).`;
    case 'F':
      return `WinAuditor : ${type} — encodé par son code fournisseur.`;
    default:
      return `WinAuditor : ${type} — encodé par son numéro.`;
  }
}

/** One role: its number and label (and a supplier's WinAuditor code), its default, and who changed it last. */
const AccountRow: React.FunctionComponent<{
  account: AccountingAccount;
  row: AccountDraft;
  onChange: (row: AccountDraft) => void;
  serverErrors: Record<string, string>;
  isDisabled: boolean;
}> = ({ account, row, onChange, serverErrors, isDisabled }) => {
  const id = `accounting-account-${account.role}`;
  const numberHelper = serverErrors[`accounts.${account.role}.number`] ?? numberError(row.number);
  const labelHelper = serverErrors[`accounts.${account.role}.label`] ?? labelError(row.label);
  const thirdPartyHelper =
    row.thirdPartyCode === null
      ? null
      : (serverErrors[`accounts.${account.role}.third_party_code`] ?? thirdPartyCodeError(row.thirdPartyCode));
  const roleError = serverErrors[`accounts.${account.role}`];
  const withCode = row.thirdPartyCode !== null;
  return (
    <FormSection title={labelOf(ACCOUNT_ROLE_LABELS, account.role)} titleElement="h3">
      <Content component="p" className="pf-v6-u-text-color-subtle pf-v6-u-font-size-sm">
        {account.description}
        {account.winauditor_type && (
          <>
            <br />
            {winauditorTypeText(account)}
          </>
        )}
      </Content>
      <Grid hasGutter>
        <GridItem md={withCode ? 3 : 4}>
          <FormGroup label="Numéro PCMN" isRequired fieldId={`${id}-number`}>
            <TextInput
              id={`${id}-number`}
              value={row.number}
              onChange={(_e, v) => onChange({ ...row, number: v })}
              inputMode="numeric"
              aria-required
              validated={numberHelper ? 'error' : 'default'}
              aria-describedby={`${id}-number-helper`}
              isDisabled={isDisabled}
            />
            <FormHelperText>
              <HelperText id={`${id}-number-helper`}>
                <HelperTextItem variant={numberHelper ? 'error' : 'default'}>
                  {numberHelper ?? `Par défaut : ${account.default_number}`}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </GridItem>
        <GridItem md={withCode ? 6 : 8}>
          <FormGroup label="Intitulé" isRequired fieldId={`${id}-label`}>
            <TextInput
              id={`${id}-label`}
              value={row.label}
              onChange={(_e, v) => onChange({ ...row, label: v })}
              aria-required
              validated={labelHelper ? 'error' : 'default'}
              aria-describedby={`${id}-label-helper`}
              isDisabled={isDisabled}
            />
            <FormHelperText>
              <HelperText id={`${id}-label-helper`}>
                <HelperTextItem variant={labelHelper ? 'error' : 'default'}>
                  {labelHelper ?? `Par défaut : ${account.default_label}`}
                </HelperTextItem>
              </HelperText>
            </FormHelperText>
          </FormGroup>
        </GridItem>
        {row.thirdPartyCode !== null && (
          <GridItem md={3}>
            <FormGroup label="Code fournisseur WinAuditor" isRequired fieldId={`${id}-third-party-code`}>
              <TextInput
                id={`${id}-third-party-code`}
                value={row.thirdPartyCode}
                onChange={(_e, v) => onChange({ ...row, thirdPartyCode: v })}
                aria-required
                validated={thirdPartyHelper ? 'error' : 'default'}
                aria-describedby={`${id}-third-party-code-helper`}
                isDisabled={isDisabled}
              />
              <FormHelperText>
                <HelperText id={`${id}-third-party-code-helper`}>
                  <HelperTextItem variant={thirdPartyHelper ? 'error' : 'default'}>
                    {thirdPartyHelper ?? `Par défaut : ${account.default_third_party_code ?? '—'}`}
                  </HelperTextItem>
                </HelperText>
              </FormHelperText>
            </FormGroup>
          </GridItem>
        )}
      </Grid>
      {roleError && <Alert variant="danger" isInline isPlain title={roleError} />}
      <div>
        <Button
          variant="link"
          isInline
          icon={<UndoIcon />}
          isDisabled={isDisabled || isAtDefault(account, row)}
          onClick={() => onChange(defaultDraft(account))}
          aria-label={`Valeur par défaut pour ${labelOf(ACCOUNT_ROLE_LABELS, account.role)}`}
        >
          Valeur par défaut
        </Button>
        {account.updated_by !== null && account.updated_at !== null && (
          <span className="pf-v6-u-ml-md pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">
            Modifié le {formatInstantBrussels(account.updated_at)}
          </span>
        )}
      </div>
    </FormSection>
  );
};

/**
 * No `elementToFocus`: PF hands it to focus-trap without a fallback, so a selector that misses while the
 * chart is still loading throws ("needs at least one focusable element"). The default first tabbable is fine.
 *
 * « Plan comptable » — the PCMN account of each role the accounting entries book on
 * (`GET` / `PATCH /admin/accounting/accounts`). Sophie keeps it in line with her WinAuditor file. Only the
 * changed roles and keys are sent (the endpoint merges, all-or-nothing); after a save the entries reload.
 */
const AccountingAccountsModal: React.FunctionComponent<AccountingAccountsModalProps> = ({
  isOpen,
  onClose,
  onSaved,
}) => {
  const { accounts, loading, error, reload } = useAccountingAccounts(isOpen);
  const { save, saving } = useSaveAccountingAccounts();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [draft, setDraft] = useState<AccountsDraft>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  // Seed the draft once per read (a new open, or Réessayer) — never while the user is editing.
  useEffect(() => {
    if (accounts !== null) {
      setDraft(draftFrom(accounts));
      setFormError(null);
      setServerErrors({});
    }
  }, [accounts]);

  const patch = useMemo(() => (accounts === null ? null : buildAccountsPatch(accounts, draft)), [accounts, draft]);
  const canSave = !saving && patch !== null && isDraftValid(draft);

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const submit = async () => {
    if (!canSave || patch === null) {
      return;
    }
    setFormError(null);
    setServerErrors({});
    const result = await save(patch);
    if (result.ok) {
      const n = Object.keys(patch.accounts).length;
      addNotification({
        title: 'Plan comptable enregistré',
        srTitle: 'Success alert',
        description: `${n} ${n === 1 ? 'compte modifié' : 'comptes modifiés'}. Les écritures du mois sont relues.`,
        variant: 'success',
      });
      setDrawerOpen(true);
      onSaved();
      onClose();
      return;
    }
    setFormError(result.message);
    setServerErrors(result.fieldErrors);
    addNotification({
      title: "Impossible d'enregistrer le plan comptable",
      srTitle: 'Error alert',
      description: result.message,
      variant: 'danger',
    });
    setDrawerOpen(true);
  };

  const renderBody = () => {
    if (accounts === null) {
      return error !== null && !loading ? (
        <Alert
          variant="danger"
          isInline
          title="Impossible de lire le plan comptable"
          actionLinks={
            <Button variant="link" isInline onClick={() => void reload()}>
              Réessayer
            </Button>
          }
        >
          {error}
        </Alert>
      ) : (
        <div className="pf-v6-u-text-align-center pf-v6-u-py-xl">
          <Spinner size="lg" aria-label="Chargement du plan comptable" />
        </div>
      );
    }
    const generalErrors = Object.entries(serverErrors).filter(([key]) => !key.startsWith('accounts.'));
    return (
      <Form
        id="accounting-accounts-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {formError && (
          <FormAlert>
            <Alert variant="danger" isInline isLiveRegion title={formError}>
              {generalErrors.length > 0 && generalErrors.map(([key, message]) => <div key={key}>{message}</div>)}
            </Alert>
          </FormAlert>
        )}
        {accounts.map((account) => {
          const row = draft[account.role];
          return row === undefined ? null : (
            <AccountRow
              key={account.role}
              account={account}
              row={row}
              onChange={(next) => setDraft((prev) => ({ ...prev, [account.role]: next }))}
              serverErrors={serverErrors}
              isDisabled={saving}
            />
          );
        })}
      </Form>
    );
  };

  return (
    <Modal
      variant="large"
      isOpen={isOpen}
      onClose={handleClose}
      aria-labelledby="accounting-accounts-title"
      aria-describedby="accounting-accounts-description"
    >
      <ModalHeader
        title="Plan comptable"
        labelId="accounting-accounts-title"
        description="Le compte PCMN de chaque rôle des écritures, à tenir en phase avec WinAuditor. Les comptes de vente viennent des catégories de produits de Facturier ; les codes des clients se saisissent dans « Codes clients WinAuditor »."
        descriptorId="accounting-accounts-description"
      />
      <ModalBody tabIndex={0} aria-label="Comptes du plan comptable">
        {renderBody()}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          type="submit"
          form="accounting-accounts-form"
          isLoading={saving}
          isDisabled={!canSave}
        >
          Enregistrer
        </Button>
        <Button variant="link" onClick={handleClose} isDisabled={saving}>
          Annuler
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AccountingAccountsModal;
